import { PublicTenant } from '@rewardops/env'

export type Sort = {
  by:
    | 'NAME'
    | 'SALE_PRICE'
    | 'MEMBER_SALE_PRICE'
    | 'CREATED_DATE'
    | 'UPDATED_DATE'
    | 'EARN_RATE'
    | 'ITEM_SAVED_AT'
  order: 'ASCENDING' | 'DESCENDING'
}

export type Filter = {
  q?: string
  categoryCode?: string // "CAT_MBN_000009"
  subCategoryCode?: string // ...
  minPrice?: number
  maxPrice?: number
  brands?: string[] // ["alienware"]
  categories?: string[] // ["CAT_WJT_000300"]
  sort?: Sort
  isFeatured?: boolean
  isAffiliate?: boolean
  isPromotion?: boolean
  promotionLabel?: string
  brandLabel?: string
  isFavourite?: boolean
  isWishlist?: boolean
  earnRateCashExpressions?: any[] // earnRateCondition: [['==', '1'], ['>', '5']]
  onlyItemsWithinBalance?: boolean
  startsWith?: string
  exclusiveRetailerOffers?: boolean
  sivIds?: string[]
  ivIds?: string[]
  isAllItems?: boolean
}

export const createFilterString = (filter: Filter, tenant?: PublicTenant) => {
  const {
    categoryCode,
    subCategoryCode = undefined,
    brands = [],
    categories = [],
    minPrice = 0,
    maxPrice = 0,
    isAllItems,
  } = filter

  const filterString =
    !filter?.isAffiliate && !isAllItems
      ? [
          '(SIV_ATTRIBUTE(type)!=["affiliate","affiliate_with_variable_category"])',
        ]
      : []

  if (categoryCode) {
    filterString.push(
      `CUSTOM_CATEGORY(true, offer_contract_segment)=="${categoryCode}"`,
    )
  }

  if (subCategoryCode) {
    filterString.push(
      `CUSTOM_CATEGORY(true, offer_contract_segment)=="${subCategoryCode}"`,
    )
  }

  if (minPrice || maxPrice) {
    const priceConditions = []

    if (minPrice !== 0) {
      priceConditions.push(`ATTRIBUTE(sale_price,member)>=${minPrice}`)
    }

    if (maxPrice !== 0) {
      priceConditions.push(`ATTRIBUTE(sale_price,member)<=${maxPrice}`)
    }

    if (priceConditions.length > 0) {
      filterString.push(`(${priceConditions.join('&')})`)
    }
  }

  if (brands.length > 0) {
    const brandConditions = brands.map((brand) => `LABEL(brand)=="${brand}"`)
    filterString.push(`(${brandConditions.join('|')})`)
  }

  if (categories.length > 0) {
    const categoryConditions = categories.map(
      (category) =>
        `CUSTOM_CATEGORY(true, offer_contract_segment)=="${category}"`,
    )
    filterString.push(`(${categoryConditions.join('|')})`)
  }

  if (filter?.isFeatured) {
    filterString.push(
      `((CUSTOM_PROPERTY("featured", offer_contract_segment)=="TRUE"))`,
    )
  }

  if (filter?.isAffiliate) {
    let affiliateFilter =
      '(SIV_ATTRIBUTE(type)==["affiliate","affiliate_with_variable_category"])&(VARIANT_PROPERTY("retailer_offer")=="base")'

    const earnRateCashFilters: string[] = []
    // earnRateCashExpressions e.g.: [['=', '1'], ['>', '5']]
    if (filter?.earnRateCashExpressions) {
      const expressions = filter?.earnRateCashExpressions

      expressions?.forEach((expression: any) => {
        earnRateCashFilters.push(
          `ATTRIBUTE(earn_rate_cash, member)${expression[0]}${expression[1]}`,
        )
      })
    }

    // Construct the filter string with a conditional ampersand
    const combinedFilter =
      earnRateCashFilters.length > 0
        ? `${affiliateFilter}&${earnRateCashFilters.join('|')}`
        : affiliateFilter

    filterString?.push(combinedFilter)
  }

  if (filter?.isPromotion) {
    filterString?.push(
      'ATTRIBUTE(sale_price, member)<ATTRIBUTE(regular_price, member)',
    )
  }

  if (filter?.brandLabel) {
    filterString?.push(`LABEL(brand)=="${filter?.brandLabel}"`)
  }

  if (filter?.promotionLabel) {
    filterString?.push(
      `CUSTOM_LABEL(promotion, offer_contract_segment)=="${filter.promotionLabel}"`,
    )
  }

  if (filter?.exclusiveRetailerOffers) {
    filterString?.shift()
    filterString?.push(
      '(SIV_ATTRIBUTE(type)==["affiliate","affiliate_with_variable_category"])&(VARIANT_PROPERTY("retailer_offer")!="base")',
    )
  }

  if (filter?.sivIds?.length) {
    filterString?.push(`(SIV_ATTRIBUTE(id)==[${filter?.sivIds?.toString()}])`)
  }

  if (filter?.ivIds?.length) {
    filterString?.push(`(SIV_ATTRIBUTE(iv_id)==[${filter?.ivIds?.toString()}])`)
  }

  if (tenant?.hiddenCategories?.length) {
    tenant?.hiddenCategories?.forEach((categoryCode) =>
      filterString.push(
        `CUSTOM_CATEGORY(true, offer_contract_segment)!="${categoryCode}"`,
      ),
    )
  }

  return filterString.join('&')
}
